/**
 * Haltungen über die Zeit: Folgen von Posen, zwischen denen weich übergeblendet
 * wird. Damit lassen sich Werkzeugbewegungen (Messer, Löffel, Besen) als
 * Abfolge von Stationen beschreiben.
 */
import { ease, lerp, segment } from '../geometry.ts';
import type { Easing } from '../geometry.ts';

/** Eine Pose: Ort, Drehung und Anheben. */
export interface Pose {
  readonly x: number;
  readonly y: number;
  readonly rotate: number;
  readonly lift: number;
}

/** Station: ab diesem Zeitpunkt wird die Pose erreicht. */
export interface Station {
  readonly at: number;
  readonly pose: Pose;
}

/** Mischt zwei Posen. */
export function mixPose(a: Pose, b: Pose, t: number): Pose {
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    rotate: lerp(a.rotate, b.rotate, t),
    lift: lerp(a.lift, b.lift, t),
  };
}

/** Pose zu einem Zeitpunkt entlang einer Folge von Stationen. */
export function poseAt(t: number, stations: readonly Station[], easing: Easing = ease.inOutCubic): Pose {
  const first = stations[0];
  if (first === undefined) {
    return { x: 0, y: 0, rotate: 0, lift: 0 };
  }
  if (t <= first.at) {
    return first.pose;
  }
  for (let index = 1; index < stations.length; index += 1) {
    const previous = stations[index - 1];
    const next = stations[index];
    if (previous !== undefined && next !== undefined && t <= next.at) {
      return mixPose(previous.pose, next.pose, easing(segment(t, previous.at, next.at)));
    }
  }
  return (stations[stations.length - 1] as Station).pose;
}
